import React, { useEffect, useState } from 'react';
import { fetchData } from '../../apiService';
import { useAuth } from '../../AuthContext';

const PALETTE = [
  { bar: "linear-gradient(90deg, #2dd4bf, #0d9488)", glow: "rgba(45,212,191,0.35)", badge: "#0d9488" },
  { bar: "linear-gradient(90deg, #818cf8, #4f46e5)", glow: "rgba(129,140,248,0.35)", badge: "#4f46e5" },
  { bar: "linear-gradient(90deg, #fb923c, #ea580c)", glow: "rgba(251,146,60,0.35)",  badge: "#ea580c" },
  { bar: "linear-gradient(90deg, #34d399, #059669)", glow: "rgba(52,211,153,0.35)",  badge: "#059669" },
  { bar: "linear-gradient(90deg, #f472b6, #db2777)", glow: "rgba(244,114,182,0.35)", badge: "#db2777" },
];

const CategorySalesChart = () => {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);
  const { companyId, token }  = useAuth();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setAnimated(false);
      const toDateObj  = new Date();
      const toDate     = toDateObj.toISOString().split('T')[0];
      const fromDateObj = new Date(toDateObj);
      fromDateObj.setMonth(fromDateObj.getMonth() - 1);
      const fromDate   = fromDateObj.toISOString().split('T')[0];
      try {
        const res = await fetchData('ReportApi', `/SalesCategoryWise/${fromDate}/${toDate}/${companyId}`, token);
        if (res.responseCode === '1111') {
          const top5 = [...res.response_data]
            .sort((a, b) => b.SaleQuantity - a.SaleQuantity)
            .slice(0, 5);
          setData(top5);
        }
      } catch (e) {
        console.error('CategorySalesChart error:', e);
      } finally {
        setLoading(false);
        // slight delay so CSS transition fires after mount
        setTimeout(() => setAnimated(true), 80);
      }
    };
    if (companyId && token) load();
  }, [companyId, token]);

  const maxVal   = data.length ? Math.max(...data.map(d => d.SaleQuantity)) : 1;
  const total    = data.reduce((s, d) => s + d.SaleQuantity, 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140 }}>
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          border: '2px solid rgba(45,212,191,0.2)',
          borderTopColor: '#2dd4bf',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div style={{ textAlign: 'center', color: '#5a7a7a', fontSize: 11, padding: '20px 0' }}>
        No data for last month
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Summary strip */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 8, padding: '5px 10px', borderRadius: 8,
        background: 'linear-gradient(90deg, rgba(45,212,191,0.08), rgba(99,102,241,0.08))',
        border: '1px solid rgba(45,212,191,0.15)',
      }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#5a7a7a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Top 5 · Last Month
        </span>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#1a2e2e' }}>
          {total.toLocaleString()} <span style={{ fontSize: 9, fontWeight: 500, color: '#5a7a7a' }}>units</span>
        </span>
      </div>

      {/* Bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {data.map((item, idx) => {
          const pct     = (item.SaleQuantity / maxVal) * 100;
          const sharePct = ((item.SaleQuantity / total) * 100).toFixed(1);
          const p       = PALETTE[idx % PALETTE.length];

          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>

              {/* Rank badge */}
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                background: p.badge, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 800, color: '#fff',
                boxShadow: `0 2px 6px ${p.glow}`,
              }}>
                {idx + 1}
              </div>

              {/* Label + bar + value */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#1a2e2e',
                    textTransform: 'uppercase', letterSpacing: '0.3px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: '65%',
                  }}>
                    {item.CategoryName}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#1a2e2e' }}>
                      {item.SaleQuantity.toLocaleString()}
                    </span>
                    <span style={{ fontSize: 9, color: '#5a7a7a' }}>{sharePct}%</span>
                  </div>
                </div>

                {/* Track */}
                <div style={{
                  width: '100%', height: 7, borderRadius: 99,
                  background: 'rgba(0,0,0,0.06)', overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    background: p.bar,
                    boxShadow: `0 0 8px ${p.glow}`,
                    width: animated ? `${pct}%` : '0%',
                    transition: `width 0.7s cubic-bezier(.4,0,.2,1) ${idx * 80}ms`,
                  }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategorySalesChart;
